import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Accordion,
  Botao,
  Breadcrumb,
  Cabecalho,
  Carregando,
  ErroLayoutContainer,
  IconeBack,
  IconeLapis,
  Link
} from 'src/componentes'
import { FuncoesDataHora, FuncoesMoeda } from 'src/componentes/funcoes'
import { RotasRescisaoAluno } from 'src/rotas/rescisao'
import { Api } from 'src/servicos'
import {
  GerarCobrancaRescisaoPayload,
  ProtocoloRescisaoDetalheDto
} from 'src/servicos/api/rescisao'
import {
  CorStatusRescisao,
  SetorRescisao,
  SetorRescisaoPorExtenso,
  StatusRescisao,
  StatusRescisaoPorExtenso
} from 'src/tipos'

import {
  ModalEditarFinanceiroRescisao,
  ModalEditarFinanceiroRescisaoRef
} from '../componentes/modal-editar-financeiro'
import {
  ModalEditarGeraisRescisao,
  ModalEditarGeraisRescisaoRef
} from '../componentes/modal-editar-gerais'
import {
  ModalEnviarTermoRescisao,
  ModalEnviarTermoRescisaoRef
} from '../componentes/modal-enviar-termo'
import {
  ModalGerarCobrancaRescisao,
  ModalGerarCobrancaRescisaoRef
} from '../componentes/modal-gerar-cobranca'
import {
  AcoesFinanceiro,
  AcoesGerais,
  BadgeStatus,
  BlocoComEdicao,
  BotaoEditarBloco,
  Campo,
  Conteudo,
  ContainerLink,
  Grade,
  ItemChat,
  ItemTimeline,
  ListaChat,
  NovaObservacao,
  Timeline,
  TituloBloco
} from './styles'

const exibirTexto =(valor?: string | number) =>
  valor === null || valor === undefined || valor === '' ? '—' : String(valor)

const exibirData = (valor?: string) =>
  valor ? FuncoesDataHora.dataStringFormatadaComBarras(valor) : '—'

const exibirMoeda = (valor?: number) =>
  valor === null || valor === undefined ? '—' : FuncoesMoeda.formatarMoeda(valor)

const CampoInfo: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({
  rotulo,
  valor
}) => (
  <Campo>
    <span className="rotulo">{rotulo}</span>
    <span className="valor">{valor}</span>
  </Campo>
)

const STATUS_SEM_REVERSAO = [
  StatusRescisao.Rescisao,
  StatusRescisao.Conversao,
  StatusRescisao.Inativo
]

// Status anteriores a "Aguardando termo de rescisão" em que já existe matrícula/valores vinculados
// ao protocolo (aluno já concluiu o wizard) — não inclui EmAberto, onde o aluno ainda não
// respondeu o formulário e portanto não há o que gerar no termo.
const STATUS_ANTERIORES_AO_TERMO = [
  StatusRescisao.EmContatoAcademico,
  StatusRescisao.EmContatoFinanceiro,
  StatusRescisao.AguardandoRetornoAluno,
  StatusRescisao.TrocaModalidade
]

export const PaginaDetalheRescisao: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const modalEditarGerais = useRef<ModalEditarGeraisRescisaoRef>(null)
  const modalEditarFinanceiro = useRef<ModalEditarFinanceiroRescisaoRef>(null)
  const modalGerarCobranca = useRef<ModalGerarCobrancaRescisaoRef>(null)
  const modalEnviarTermo = useRef<ModalEnviarTermoRescisaoRef>(null)
  const [protocolo, definirProtocolo] = useState<ProtocoloRescisaoDetalheDto>(
    null
  )
  const [carregando, definirCarregando] = useState(true)
  const [revertendo, definirRevertendo] = useState(false)
  const [novaObservacao, definirNovaObservacao] = useState('')
  const [salvandoObservacao, definirSalvandoObservacao] = useState(false)
  const [gerandoCobranca, definirGerandoCobranca] = useState(false)
  const [recalculando, definirRecalculando] = useState(false)
  const [enviandoFormulario, definirEnviandoFormulario] = useState(false)

  const carregar = async () => {
    try {
      definirCarregando(true)
      const dados = await Api.ObterProtocoloRescisaoPorId(id)
      definirProtocolo(dados)
    } catch {
      toast('Erro ao carregar o protocolo de rescisão.', { type: 'error' })
    } finally {
      definirCarregando(false)
    }
  }

  useEffect(() => {
    carregar()
  }, [id]) //eslint-disable-line

  const adicionarObservacao = async () => {
    if (!novaObservacao.trim()) return

    try {
      definirSalvandoObservacao(true)
      await Api.AdicionarObservacaoInterna(id, novaObservacao.trim())
      definirNovaObservacao('')
      await carregar()
    } finally {
      definirSalvandoObservacao(false)
    }
  }

  const reverterRescisao = async () => {
    try {
      definirRevertendo(true)
      await Api.ReverterRescisao(id)
      toast('Rescisão revertida com sucesso!', { type: 'success' })
      await carregar()
    } finally {
      definirRevertendo(false)
    }
  }

  const copiarLink = async (rota: string, descricao: string) => {
    const url = `${window.location.origin}${rota}/${id}`
    try {
      await navigator.clipboard.writeText(url)
      toast(`Link do ${descricao} copiado!`, { type: 'success' })
    } catch {
      toast(`Não foi possível copiar o link do ${descricao}.`, { type: 'error' })
    }
  }

  const abrirModalGerarCobranca = () => {
    modalGerarCobranca.current?.abrir(protocolo?.totalAPagar)
  }

  const confirmarGerarCobranca = async (dados: GerarCobrancaRescisaoPayload) => {
    try {
      definirGerandoCobranca(true)
      await Api.GerarCobrancaRescisao(id, dados)
      toast('Cobrança de rescisão gerada com sucesso!', { type: 'success' })
      await carregar()
    } finally {
      definirGerandoCobranca(false)
    }
  }

  const recalcularValores = async () => {
    try {
      definirRecalculando(true)
      await Api.RecalcularValoresRescisao(id)
      toast('Valores recalculados com sucesso!', { type: 'success' })
      await carregar()
    } finally {
      definirRecalculando(false)
    }
  }

  const enviarFormulario = async () => {
    try {
      definirEnviandoFormulario(true)
      await Api.EnviarFormularioRescisao(id)
      toast('Formulário enviado ao aluno!', { type: 'success' })
      await carregar()
    } finally {
      definirEnviandoFormulario(false)
    }
  }

  if (carregando) {
    return <Carregando texto="Carregando protocolo de rescisão..." />
  }

  if (!protocolo) return null

  const chaveStatus = protocolo.status as keyof typeof StatusRescisao
  const corStatus = CorStatusRescisao[chaveStatus]
  const reversaoDesabilitada =
    revertendo ||
    STATUS_SEM_REVERSAO.some(status => status === protocolo.status)

  return (
    <>
      <ContainerLink>
        <Link
          texto="Voltar"
          acaoVoltar={() => history.goBack()}
          icone={IconeBack}
        />
      </ContainerLink>
      <Cabecalho>
        <Breadcrumb
          titulo={`Protocolo ${protocolo.numeroProtocolo}`}
          atalhos={[
            { texto: 'Rescisões', acao: () => history.goBack() },
            { texto: protocolo.numeroProtocolo }
          ]}
        />
        <Botao
          type="button"
          id="btn-reverter-rescisao"
          texto="Reverter rescisão"
          tema="Secundario"
          disabled={reversaoDesabilitada}
          carregando={revertendo}
          onClick={reverterRescisao}
        />
      </Cabecalho>
      <Conteudo>
        {/* ==================== BLOCO GERAIS ==================== */}
        <Accordion titulo="Gerais" iniciarAberto>
          <BlocoComEdicao>
            <BotaoEditarBloco
              type="button"
              title="Editar"
              onClick={() => modalEditarGerais.current?.abrir(protocolo)}
            >
              {IconeLapis}
            </BotaoEditarBloco>
            <Grade>
              <CampoInfo
                rotulo="Status"
                valor={
                  corStatus ? (
                    <BadgeStatus
                      fundo={corStatus.fundo}
                      corTexto={corStatus.texto}
                    >
                      {StatusRescisaoPorExtenso[chaveStatus] ?? protocolo.status}
                    </BadgeStatus>
                  ) : (
                    exibirTexto(protocolo.status)
                  )
                }
              />
              <CampoInfo
                rotulo="Setor responsável"
                valor={
                  protocolo.setorResponsavel
                    ? SetorRescisaoPorExtenso[
                        protocolo.setorResponsavel as keyof typeof SetorRescisao
                      ] ?? protocolo.setorResponsavel
                    : '—'
                }
              />
              <CampoInfo rotulo="Aluno" valor={exibirTexto(protocolo.nomeAluno)} />
              <CampoInfo rotulo="CPF" valor={exibirTexto(protocolo.cpf)} />
              <CampoInfo rotulo="E-mail" valor={exibirTexto(protocolo.email)} />
              <CampoInfo rotulo="Contato" valor={exibirTexto(protocolo.contato)} />
              <CampoInfo
                rotulo="Modalidade (somente leitura)"
                valor={exibirTexto(protocolo.modalidade)}
              />
              <CampoInfo
                rotulo="Curso (somente leitura)"
                valor={exibirTexto(protocolo.curso)}
              />
              <CampoInfo rotulo="Motivo" valor={exibirTexto(protocolo.motivo)} />
              {protocolo.motivo === 'Outro' && (
                <CampoInfo
                  rotulo="Motivo customizado"
                  valor={exibirTexto(protocolo.motivoOutroTexto)}
                />
              )}
              <CampoInfo
                rotulo="Caso especial"
                valor={protocolo.casoEspecial ? 'Sim' : 'Não'}
              />
              <CampoInfo
                rotulo="Data de abertura"
                valor={exibirData(protocolo.dataAbertura)}
              />
              <CampoInfo
                rotulo="Data de fim"
                valor={exibirData(protocolo.dataFimProtocolo)}
              />
              <CampoInfo
                rotulo="Data do 1º contato"
                valor={exibirData(protocolo.dataPrimeiroContato)}
              />
              <CampoInfo
                rotulo="Motivo detalhado"
                valor={exibirTexto(protocolo.motivoDetalhado)}
              />
            </Grade>
            <AcoesGerais>
              {protocolo.status === StatusRescisao.EmAberto && (
                <Botao
                  type="button"
                  id="btn-enviar-formulario"
                  texto="Enviar formulário"
                  carregando={enviandoFormulario}
                  onClick={enviarFormulario}
                />
              )}
              {protocolo.status ===
                StatusRescisao.AguardandoRespostaFormulario && (
                <Botao
                  type="button"
                  id="btn-copiar-link-wizard"
                  texto="Copiar link do formulário"
                  tema="Secundario"
                  onClick={() =>
                    copiarLink(RotasRescisaoAluno.Wizard, 'formulário')
                  }
                />
              )}
              {protocolo.status === StatusRescisao.AguardandoTermoRescisao && (
                <Botao
                  type="button"
                  id="btn-copiar-link-termo"
                  texto="Copiar link do termo"
                  tema="Secundario"
                  onClick={() => copiarLink(RotasRescisaoAluno.Termo, 'termo')}
                />
              )}
              {STATUS_ANTERIORES_AO_TERMO.some(
                status => status === protocolo.status
              ) && (
                <Botao
                  type="button"
                  id="btn-enviar-termo-rescisao"
                  texto="Enviar termo de rescisão"
                  onClick={() => modalEnviarTermo.current?.abrir(id)}
                />
              )}
            </AcoesGerais>
          </BlocoComEdicao>
        </Accordion>

        {/* ==================== BLOCO FINANCEIRO ==================== */}
        <Accordion titulo="Financeiro" iniciarAberto={false}>
          <BlocoComEdicao>
            <BotaoEditarBloco
              type="button"
              title="Editar"
              onClick={() => modalEditarFinanceiro.current?.abrir(protocolo)}
            >
              {IconeLapis}
            </BotaoEditarBloco>
            <Grade>
              <CampoInfo
                rotulo="Parcelamento"
                valor={exibirTexto(protocolo.parcelamento)}
              />
              <CampoInfo
                rotulo="Forma de pagamento do curso"
                valor={exibirTexto(protocolo.formaPagamentoCurso)}
              />
              <CampoInfo
                rotulo="Nº parcelas pagas"
                valor={exibirTexto(protocolo.nrParcelasPagas)}
              />
              <CampoInfo
                rotulo="Valores pagos"
                valor={exibirMoeda(protocolo.valoresPagos)}
              />
              <CampoInfo
                rotulo="Nº parcelas pendentes"
                valor={exibirTexto(protocolo.nrParcelasPendentes)}
              />
              <CampoInfo
                rotulo="Valores pendentes"
                valor={exibirMoeda(protocolo.valoresPendentes)}
              />
              <CampoInfo
                rotulo="Nº parcelas a vencer"
                valor={exibirTexto(protocolo.nrParcelasAVencer)}
              />
              <CampoInfo
                rotulo="Valores a vencer"
                valor={exibirMoeda(protocolo.valoresAVencer)}
              />
              <CampoInfo
                rotulo="% Taxa de rescisão"
                valor={
                  protocolo.percentualTaxaRescisao !== null &&
                  protocolo.percentualTaxaRescisao !== undefined
                    ? `${protocolo.percentualTaxaRescisao}%`
                    : '—'
                }
              />
              <CampoInfo
                rotulo="Multa rescisória"
                valor={exibirMoeda(protocolo.multaRescisoria)}
              />
              <CampoInfo
                rotulo="Total a pagar"
                valor={exibirMoeda(protocolo.totalAPagar)}
              />
              <CampoInfo
                rotulo="Forma de pagamento da rescisão"
                valor={exibirTexto(protocolo.formaPagamentoRescisao)}
              />
              <CampoInfo
                rotulo="Status da parcela de rescisão"
                valor={exibirTexto(protocolo.statusParcelaRescisao)}
              />
            </Grade>
          </BlocoComEdicao>
          <AcoesFinanceiro>
            {!protocolo.cobrancaRescisaoId && (
              <Botao
                type="button"
                id="btn-recalcular-valores-rescisao"
                texto="Recalcular valores"
                tema="Secundario"
                carregando={recalculando}
                onClick={recalcularValores}
              />
            )}
            {protocolo.cobrancaRescisaoId ? (
              <span>Cobrança de rescisão já gerada para este protocolo.</span>
            ) : protocolo.totalAPagar === 0 ? (
              <span>
                Não há valores a pagar — mova o status para &quot;Rescisão&quot; para efetivar.
              </span>
            ) : (
              <Botao
                type="button"
                id="btn-gerar-cobranca-rescisao"
                texto="Gerar cobrança de rescisão"
                disabled={protocolo.status !== StatusRescisao.TermoAceito}
                carregando={gerandoCobranca}
                onClick={abrirModalGerarCobranca}
                tooltip={
                  protocolo.status !== StatusRescisao.TermoAceito
                    ? {
                        id: 'tooltip-gerar-cobranca-rescisao',
                        lado: 'top',
                        texto:
                          'Só é possível gerar a cobrança depois que o aluno aceitar o Termo de Rescisão.'
                      }
                    : undefined
                }
              />
            )}
          </AcoesFinanceiro>
        </Accordion>

        {/* ==================== BLOCO CONTROLES ==================== */}
        <Accordion titulo="Controles" iniciarAberto={false}>
          <TituloBloco>Observações internas</TituloBloco>
          <ListaChat>
            {protocolo.observacoes.length === 0 && (
              <span>Nenhuma observação registrada.</span>
            )}
            {protocolo.observacoes.map(obs => (
              <ItemChat key={obs.id}>
                <div className="cabecalho">
                  {obs.nomeUsuario ?? 'Usuário'} ·{' '}
                  {FuncoesDataHora.dataHoraToLocaleString(obs.dataHora)}
                </div>
                <div className="texto">{obs.texto}</div>
              </ItemChat>
            ))}
          </ListaChat>
          <NovaObservacao>
            <textarea
              placeholder="Escreva uma observação interna..."
              value={novaObservacao}
              onChange={e => definirNovaObservacao(e.target.value)}
            />
            <Botao
              texto="Adicionar observação"
              type="button"
              carregando={salvandoObservacao}
              disabled={!novaObservacao.trim()}
              onClick={adicionarObservacao}
            />
          </NovaObservacao>

          <TituloBloco>Histórico de status</TituloBloco>
          <Timeline>
            {protocolo.historicoStatus.length === 0 && (
              <span>Nenhuma alteração de status registrada.</span>
            )}
            {protocolo.historicoStatus.map(hist => {
              const novo = hist.statusNovo as keyof typeof StatusRescisao
              const anterior = hist.statusAnterior as keyof typeof StatusRescisao
              return (
                <ItemTimeline key={hist.id}>
                  <span className="titulo">
                    {hist.statusAnterior
                      ? `${StatusRescisaoPorExtenso[anterior] ??
                          hist.statusAnterior} → `
                      : ''}
                    {StatusRescisaoPorExtenso[novo] ?? hist.statusNovo}
                  </span>
                  <span className="meta">
                    {hist.nomeUsuario ? `${hist.nomeUsuario} · ` : ''}
                    {FuncoesDataHora.dataHoraToLocaleString(hist.dataHora)}
                  </span>
                </ItemTimeline>
              )
            })}
          </Timeline>
        </Accordion>
      </Conteudo>
      <ModalEditarGeraisRescisao
        ref={modalEditarGerais}
        backdrop
        aoSalvar={() => {
          toast('Dados gerais atualizados com sucesso!', { type: 'success' })
          carregar()
        }}
      />
      <ModalEditarFinanceiroRescisao
        ref={modalEditarFinanceiro}
        backdrop
        aoSalvar={() => {
          toast('Dados financeiros atualizados com sucesso!', {
            type: 'success'
          })
          carregar()
        }}
      />
      <ModalGerarCobrancaRescisao
        ref={modalGerarCobranca}
        backdrop
        acaoPrimaria={confirmarGerarCobranca}
      />
      <ModalEnviarTermoRescisao
        ref={modalEnviarTermo}
        backdrop
        aoEnviar={() => {
          toast('Termo de rescisão enviado ao aluno!', { type: 'success' })
          carregar()
        }}
      />
    </>
  )
}

export default (): JSX.Element => (
  <ErroLayoutContainer.Provider>
    <PaginaDetalheRescisao />
  </ErroLayoutContainer.Provider>
)
